import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import Button from "../../components/Buttons/Button";
import { Toast } from "primereact/toast";
import {
  downloadStudentLogsXlsx,
  fetchStudentLogs,
} from "../../services/studentService/studentService";
import { StudentCourseActionLog } from "../../services/studentService/studentService.types";
import {
  PageContainer,
  ContentWrapper,
  Header,
  TitleBlock,
  Title,
  HeaderActions,
  BreadcrumbsContainer,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
} from "./StudentLogsPage.styles";
import { pxToRem } from "../../styles/utils";
import BreadcrumbLink from "../../components/BreadcrumbLink/BreadcrumbLink";

const rowAmountOptions = [
  { label: "10 записей", value: 10 },
  { label: "20 записей", value: 20 },
  { label: "50 записей", value: 50 },
];

const StudentLogsPage: React.FC = () => {
  const { studentId, courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const courseNameFromQuery = searchParams.get("courseName");
  const studentNameFromQuery = searchParams.get("studentName");
  const studentUsernameFromQuery = searchParams.get("studentUsername");

  const [logs, setLogs] = useState<StudentCourseActionLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [ordering, setOrdering] = useState<string>("-created");
  const [resolvedCourseName, setResolvedCourseName] = useState<string | null>(courseNameFromQuery);
  const [resolvedStudentName, setResolvedStudentName] = useState<string | null>(studentNameFromQuery);
  const [resolvedStudentUsername, setResolvedStudentUsername] = useState<string | null>(studentUsernameFromQuery);

  const headers = [
    { label: "Дата и время", key: "created", fieldName: "created" },
    { label: "Действие", key: "action", fieldName: "action" },
    { label: "Номер урока", key: "lesson_number", fieldName: "lesson_number" },
    { label: "Название урока", key: "lesson_title", fieldName: "lesson_title" },
    { label: "Название ДЗ", key: "assignment_title", fieldName: "assignment_title" },
    { label: "Имя файла", key: "material_filename", fieldName: "material_filename" },
  ];

  useEffect(() => {
    if (!studentId || !courseId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Некорректные параметры адреса",
        life: 4000,
      });
      navigate("/students");
      return;
    }

    const loadLogs = async () => {
      setLoading(true);
      try {
        const response = await fetchStudentLogs({
          studentId,
          courseId,
          page: currentPage,
          pageSize,
          ordering,
        });

        setLogs(response.results);
        setTotalPages(Math.max(1, Math.ceil(response.count / pageSize)));

        if (!resolvedCourseName || !resolvedStudentName || !resolvedStudentUsername) {
          const firstLog = response.results[0];
          if (firstLog) {
            setResolvedCourseName((prev) => prev || firstLog.course_name);
            setResolvedStudentName((prev) => prev || firstLog.student_name || String(firstLog.student_tg_id));
            setResolvedStudentUsername((prev) => prev || firstLog.student_tg_username || null);
          }
        }
      } catch (error) {
        console.error("Ошибка при загрузке логов ученика:", error);
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить логи ученика",
          life: 4000,
        });
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [
    studentId,
    courseId,
    currentPage,
    pageSize,
    ordering,
    navigate,
    resolvedCourseName,
    resolvedStudentName,
    resolvedStudentUsername,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    setOrdering((prev) => {
      if (prev === "-created" && field === "created") {
        return "created";
      }

      if (prev === field) {
        return `-${field}`;
      }

      if (prev === `-${field}`) {
        return "-created";
      }

      return field;
    });
    setCurrentPage(1);
  };

  const handleDownload = async () => {
    if (!studentId || !courseId) {
      return;
    }

    try {
      const blob = await downloadStudentLogsXlsx({
        studentId,
        courseId,
        ordering,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const safeStudent = resolvedStudentName || `student_${studentId}`;
      const safeCourse = resolvedCourseName || `course_${courseId}`;
      link.download = `logs_${safeStudent}_${safeCourse}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Ошибка при скачивании логов ученика:", error);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось скачать отчёт",
        life: 4000,
      });
    }
  };

  const rows = logs.map((log) => ({
    created: new Date(log.created).toLocaleString(),
    action: log.action,
    lesson_number: log.lesson_number ?? "-",
    lesson_title: log.lesson_title ?? "-",
    assignment_title: log.assignment_title ?? "-",
    material_filename: log.material_filename ?? "-",
  }));

  const breadcrumbParts: string[] = [];
  if (resolvedStudentName) {
    breadcrumbParts.push(`Студент: ${resolvedStudentName}`);
  }
  if (studentId) {
    breadcrumbParts.push(`TG ID: ${studentId}`);
  }
  if (resolvedStudentUsername) {
    const formattedNickname = resolvedStudentUsername.startsWith("@")
      ? resolvedStudentUsername
      : `@${resolvedStudentUsername}`;
    breadcrumbParts.push(`Ник в TG: ${formattedNickname}`);
  }
  if (resolvedCourseName) {
    breadcrumbParts.push(`Курс: ${resolvedCourseName}`);
  }
  const breadcrumbCurrentLabel = breadcrumbParts.join(" • ") || "Действия ученика";

  return (
    <PageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        <Header>
          <TitleBlock>
            <BreadcrumbsContainer>
              <BreadcrumbLink href="/students" text="Ученики" />
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbCurrent>{breadcrumbCurrentLabel}</BreadcrumbCurrent>
            </BreadcrumbsContainer>
            <Title>Действия ученика</Title>
          </TitleBlock>
          <HeaderActions>
            <Button
              type="button"
              variant="active"
              width={`${pxToRem(195)}`}
              height={`${pxToRem(50)}`}
              onClick={handleDownload}
            >
              Скачать в Excel
            </Button>
          </HeaderActions>
        </Header>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
        />
      </ContentWrapper>
    </PageContainer>
  );
};

export default StudentLogsPage;

